import { createEffect, createMemo, createSignal, mergeProps, Show } from "solid-js";
import SelectMenuWithSearch from "./SelectMenuWithSearch";
import { flattenOptions } from "../../utils/menu/searchableMenuUtil";
import { isEmpty, isEqual, isNil } from "lodash-es";
import type { ExtendableInputProps } from "../inputs/Input";
import Input from "../inputs/Input";
import SingleSelectTrigger from "./triggers/SingleSelectTrigger";
import type { FlattenedSelectOption, SelectOption, SelectOptions } from "../../types/Menu";

type SingleSelectWithSearchProps<T> = {
  id: string;
  ariaLabel: string;
  options: SelectOptions<T>;
  value?: SelectOption<T>["value"] | null;
  onSelect: (value: SelectOption<T>["value"] | null, flattenedOptions: FlattenedSelectOption<T>[]) => void;
  onClear?: () => void;
  onClose?: () => void;
  width: "matchTrigger" | "240px" | "480px";
  disabled?: boolean;
  placeholder?: string;
  errorMessage?: string;
  hideTrigger?: boolean;
};

const SingleSelectWithSearch = <T,>(rawProps: SingleSelectWithSearchProps<T>) => {
  const props = mergeProps({ disabled: false }, rawProps);

  let triggerRef!: HTMLButtonElement | null;
  const [selectedOption, setSelectedOption] = createSignal<SelectOption<T> | null>();
  const flattenedOptions = createMemo(() => flattenOptions(props.options));

  createEffect(() => {
    if (isEmpty(props.value)) {
      setSelectedOption(null);
      return;
    }
    const option = flattenedOptions().find((option) => isEqual(option.value, props.value));
    setSelectedOption(option);
    if (isNil(option)) {
      props.onSelect(null, flattenedOptions());
    }
  });

  const handleOptionSelect = (option: SelectOption<T>): void => {
    setSelectedOption(option);
    props.onSelect(option.value, flattenedOptions());
  };

  const handleClear = () => {
    setSelectedOption(null);
    props.onClear?.();
    triggerRef?.focus();
  };

  const showClearButton = createMemo(() => !isEmpty(props.value) && !isNil(props.onClear) && !props.disabled);

  return (
    <SelectMenuWithSearch
      {...props}
      Trigger={(triggerProps) => (
        <Show
          when={isNil(props.hideTrigger) || !props.hideTrigger}
          fallback={
            <button
              data-id="hidden-dynamic-value-single-select-trigger"
              class="absolute bottom-0 w-full max-w-170"
              disabled={props.disabled}
              classList={{
                "inset-x-0 h-9": props.width === "matchTrigger",
                "left-0 h-4": props.width !== "matchTrigger",
              }}
              {...triggerProps}
            />
          }
        >
          <SingleSelectTrigger
            ariaLabel={props.ariaLabel}
            value={selectedOption()}
            withClearButton={showClearButton()}
            onClear={handleClear}
            placeholder={props.placeholder}
            hasError={!isNil(props.errorMessage)}
            disabled={props.disabled}
            arkProps={triggerProps}
          />
        </Show>
      )}
      flattenedOptions={flattenedOptions()}
      selectedOptions={!isNil(selectedOption()) ? [selectedOption()!] : []}
      onSelect={(option) => handleOptionSelect(option)}
      onClose={() => {
        props.onClose?.();
        triggerRef?.focus();
      }}
      multiple={false}
    />
  );
};

type SingleSelectWithSearchInputProps<T> = Omit<SingleSelectWithSearchProps<T>, "id" | "ariaLabel"> &
  ExtendableInputProps;
export const SingleSelectWithSearchInput = <T,>(props: SingleSelectWithSearchInputProps<T>) => (
  <Input required={props.required} errorMessage={props.errorMessage} info={props.info} label={props.label}>
    {(id) => <SingleSelectWithSearch id={id} ariaLabel={props.label} {...props} />}
  </Input>
);

export default SingleSelectWithSearch;
